import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Person, SelfProfile } from '../../api/people';
import { PersonSheet, SelfPhotosSheet } from '../shoot/kit/PersonSheet';
import { uiFormat, uiText } from '../../i18n';
import { platform } from '../../platform';
import { shake } from '../../ui/motion';
import { artworkUrl } from '../studio/studio-art-model';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioSheet } from '../studio/StudioSheet';
import {
  assign,
  castCount,
  ineligible,
  roleCards,
  roleOf,
  roleTip,
  type Cast,
  type Performer,
  type ReelsRole,
} from './reels-cast';

/** Персонажей в ряду роли — как у эталона (CH_N bot-people.js); остальные — в каталоге «Все» */
const CHARS_IN_ROW = 6;

function Face({ who, cls = '' }: { who?: Performer; cls?: string }) {
  const url = artworkUrl(who?.image);
  const self = who?.type === 'me' || who?.type === 'avatar';
  return (
    <i className={`${cls}${who && !who.ready ? ' np' : ''}` || undefined}>
      {url ? <img src={url} alt="" /> : <span className="ini">{self ? <StudioIcon kind="user" /> : (who?.name ?? '').slice(0, 1)}</span>}
    </i>
  );
}
/** Отметка «в роли» в круге ряда (ic('tickS') эталона) */
function Tick() {
  return (
    <i className="ck" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 12 5 5L20 7" />
      </svg>
    </i>
  );
}
/** Силуэт пустой роли (GLYPH эталона) */
function Glyph() {
  return (
    <svg viewBox="0 0 56 56" aria-hidden="true">
      <circle cx="28" cy="22" r="8.5" />
      <path d="M11 57c0-12 7.6-18.5 17-18.5S45 45 45 57z" />
    </svg>
  );
}
/** Ряд, края которого растворяются там, где за краем есть продолжение (BotCore.fade, режим классов .fl / .fr) */
function FadeRow({ className, label, watch, children }: { className: string; label: string; watch: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ l: false, r: false });
  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { l: el.scrollLeft > 2, r: el.scrollLeft < max - 2 };
    setEdge(was => (was.l === next.l && was.r === next.r ? was : next));
  };
  useLayoutEffect(measure, [watch]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`${className}${edge.l ? ' fl' : ''}${edge.r ? ' fr' : ''}`} role="group" aria-label={label} onScroll={measure}>
      {children}
    </div>
  );
}

/** «Кто в кадре» по ролям (APP-SPEC-5 §4.4 «Роли в форме», §8.2): карточки ролей 2 × 2, строка «Кто играет»,
    ряд лиц выбранной роли, каталог персонажей роли и лист «Люди». Разметка — эталона (bot-people.js: lblHTML,
    castCardsHTML, castQHTML, roleRowHTML), стили — bot-people.css. */
export function ReelsCast({
  roles,
  cast,
  performers,
  focus,
  locked,
  groups,
  onFocus,
  onChange,
  self = null,
  onReload,
}: {
  /** Свои фото «Вы» — для листа «Ваши фото» */
  self?: SelfProfile | null;
  /** Перечитать людей после сохранения в листе */
  onReload?(): void;
  roles: ReelsRole[];
  cast: Cast;
  performers: Performer[];
  focus: string;
  locked: boolean;
  /** Набор шаблонов: подпись группы роли по id шаблонов (названия) */
  groups?: Record<string, string>;
  onFocus(roleId: string): void;
  onChange(cast: Cast, focus: string): void;
}) {
  const [hint, setHint] = useState('');
  const [sheet, setSheet] = useState<'people' | 'chars' | 'self' | 'person' | null>(null);
  const [editing, setEditing] = useState<Person | null>(null);
  const pending = useRef<{ ref: string; role: string } | null>(null);
  // Близкий после «Добавьте фото» и «Вы» после «Ваши фото» сразу встают в роль, когда сервер подтвердил готовность.
  useEffect(() => {
    const wanted = pending.current;
    if (!wanted) return;
    const ready = performers.find(p => p.ref === wanted.ref && p.ready);
    if (!ready) return;
    pending.current = null;
    const target = roles.find(r => r.id === wanted.role);
    if (!target || ineligible(target, ready) || (cast[target.id] ?? []).includes(ready.ref)) return;
    const result = assign(roles, cast, target.id, ready.ref, performers);
    if (!result.error) onChange(result.cast, result.focus);
  }, [performers, roles, cast, onChange]);
  const [query, setQuery] = useState('');
  const hintRef = useRef<HTMLParagraphElement>(null);
  // Подсказка «почему нельзя» встаёт под рядом и вздрагивает (строки нет, пока сказать нечего).
  const [nudge, setNudge] = useState(0);
  useEffect(() => {
    if (nudge) shake(hintRef.current);
  }, [nudge]);
  const role = roles.find(r => r.id === focus) ?? roles[0];
  if (!role) return null;
  const who = (ref: string) => performers.find(p => p.ref === ref);
  const charNames = (ids: number[]) => ids.map(id => who(`character:${id}`)?.name).filter(Boolean).join(' и ');
  const say = (text: string) => {
    setHint(text);
    setNudge(value => value + 1);
    platform.haptic('light');
  };
  const tap = (ref: string, close = false) => {
    if (locked) return;
    const performer = who(ref);
    if (!performer) return;
    const already = (cast[role.id] ?? []).includes(ref);
    const reason = already ? null : ineligible(role, performer, charNames);
    if (reason) return say(reason);
    // Близкий без фото и неготовый «Вы» в роль не встают: сначала фото (профиль «Близкие» или лист «Вы»).
    if (!already && !performer.ready) {
      pending.current = { ref, role: role.id };
      if (performer.type === 'me') return setSheet('self');
      if (performer.person) {
        setEditing(performer.person);
        return setSheet('person');
      }
      return say(performer.notReady ?? uiFormat('Добавьте фото: {0}', performer.name));
    }
    const result = assign(roles, cast, role.id, ref, performers);
    if (result.error) return say(result.error);
    setHint('');
    onChange(result.cast, result.focus);
    if (close && role.max === 1) setSheet(null);
  };
  const playing = (p: Performer) => {
    const other = roleOf(cast, p.ref);
    return other && other !== role.id ? roles.find(r => r.id === other)?.name : undefined;
  };
  // Ряд роли, как у эталона (bot-people.js roleRowHTML): «Вы» (если роль берёт взрослых), подходящие близкие,
  // «Добавить», черта и персонажи роли (первые CHARS_IN_ROW подходящих, уже стоящие в роли — первыми) и «Все».
  const inRole = cast[role.id] ?? [];
  const fitting = (p: Performer) => !ineligible(role, p, charNames) || inRole.includes(p.ref);
  const me = performers.find(p => p.type === 'me' || p.type === 'avatar');
  const people = performers.filter(p => p.type === 'person' && fitting(p));
  const chars = performers.filter(p => p.type === 'char');
  const fitChars = chars.filter(p => !ineligible(role, p, charNames));
  const firstChars = fitChars.slice(0, CHARS_IN_ROW);
  const rowChars = role.allowChar ? [...chars.filter(p => inRole.includes(p.ref) && !firstChars.includes(p)), ...firstChars] : [];
  const sortedChars = [...fitChars, ...chars.filter(p => !fitChars.includes(p))].filter(p =>
    p.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const cards = roleCards(roles);
  const byGroup = new Map<string, ReelsRole[]>();
  for (const r of roles) {
    const label = !groups ? '' : r.templates.length > 1 || r.templates.length === Object.keys(groups).length ? 'Во всех шаблонах' : r.templates.map(id => groups[id] ?? id).join(' · ');
    byGroup.set(label, [...(byGroup.get(label) ?? []), r]);
  }
  const card = (r: ReelsRole) => {
    const refs = cast[r.id] ?? [];
    const on = r.id === role.id;
    const sub = refs.length
      ? refs.map(ref => (who(ref)?.type === 'me' || who(ref)?.type === 'avatar' ? 'Вы' : (who(ref)?.name ?? ''))).join(', ')
      : on
        ? 'Выберите ниже'
        : r.min > 0
          ? 'Нужно выбрать'
          : 'По желанию';
    return (
      <button
        key={r.id}
        type="button"
        className={`pp-rc${on ? ' on' : ''}${refs.length ? '' : ' vac'}`}
        aria-pressed={on}
        aria-label={`${uiText(r.name)}: ${uiText(refs.length ? sub : r.min > 0 ? 'нужно выбрать' : 'по желанию')}`}
        onClick={() => {
          setHint('');
          onFocus(r.id);
        }}
      >
        <span className="pp-rav">
          {refs.length ? (
            <>
              {(refs.length > 3 ? refs.slice(0, 2) : refs).map(ref => (
                <Face key={ref} who={who(ref)} />
              ))}
              {refs.length > 3 && (
                <i className="more">
                  <span className="mono">+{refs.length - 2}</span>
                </i>
              )}
            </>
          ) : (
            <i className={`pp-gh${r.min ? '' : ' opt'}`}>
              <Glyph />
            </i>
          )}
        </span>
        <span className="pp-rtx">
          <b>
            {uiText(r.name)}
            {r.max > 1 &&
              (refs.length ? (
                <em className="mono">{`${refs.length}/${r.max}`}</em>
              ) : (
                <em>
                  {uiText('до')} <span className="mono">{r.max}</span>
                </em>
              ))}
          </b>
          <small>{uiText(sub)}</small>
        </span>
      </button>
    );
  };
  /** Круг ряда (fcHTML эталона): кольцо — уже в этой роли, под именем — роль, которую играет сейчас; без фото — точка */
  const face = (p: Performer, key: string) => {
    const on = inRole.includes(p.ref);
    const other = playing(p);
    const self = p.type === 'me' || p.type === 'avatar';
    const url = artworkUrl(p.image);
    const name = self ? uiText('Вы') : p.name;
    return (
      <button
        key={key}
        type="button"
        className={`ps-fc${on ? ' on' : ''}${!p.ready && !on ? ' np' : ''}`}
        data-k={key}
        aria-pressed={on}
        aria-label={`${name}${other ? uiFormat(', играет: {0}', other) : ''}${p.ready ? '' : uiText(self ? ': добавьте свои фото' : ': добавьте фото')}`}
        disabled={locked}
        onClick={() => tap(p.ref)}
      >
        <span className="ps-fo">
          <span className="ps-fi">
            {url ? <img src={url} alt="" draggable={false} /> : <span className="ini">{self ? <StudioIcon kind="user" /> : p.name.slice(0, 1)}</span>}
          </span>
          <Tick />
          {!p.ready && (!self || on) && <i className="dot" aria-hidden="true" />}
        </span>
        <b>{name}</b>
        {other && <small>{uiText(other)}</small>}
      </button>
    );
  };
  const tool = (key: 'add' | 'all', label: string, aria: string, onClick: () => void) => (
    <button key={key} type="button" className={`ps-fc ${key}`} data-k={key} aria-pressed={false} aria-label={uiText(aria)} disabled={locked} onClick={onClick}>
      <span className="ps-fo">
        <span className="ps-fi">
          <span className={key === 'add' ? 'ini plus' : 'ini'}>
            <StudioIcon kind={key === 'add' ? 'plus' : 'chev'} />
          </span>
        </span>
        <Tick />
      </span>
      <b>{uiText(label)}</b>
    </button>
  );
  return (
    <div className="rl-w">
      <div className="glbl ps-fl">
        <b className="pp-lt">
          {uiText('Кто в кадре')}
          <em className="pp-ln">{` · ${uiText(castCount(roles, cast))}`}</em>
        </b>
        <button type="button" className="pp-chg" aria-label={uiText('Изменить: Кто в кадре')} disabled={locked} onClick={() => setSheet('people')}>
          <StudioIcon kind="edit" />
          {uiText('Изменить')}
        </button>
      </div>
      <div className="ps-face pp-castc">
        {cards &&
          [...byGroup.entries()].map(([label, list]) => (
            <Fragment key={label || 'roles'}>
              {label && <div className="ps-grp">{uiText(label)}</div>}
              <div className={`pp-rcs${byGroup.size === 1 && list.length === 1 ? ' one' : ''}`} role="group" aria-label={uiText(label || 'Роли')}>
                {list.map(card)}
              </div>
            </Fragment>
          ))}
        <div className="pp-q">
          <span>
            {cards || role.allowChar ? (
              <>
                {uiText('Кто играет:')} <b>{uiText(role.name)}</b>
              </>
            ) : (
              uiText('Можно себя, близких или персонажа')
            )}
          </span>
          {role.allowChar && chars.length ? (
            <button type="button" className="pp-chl" disabled={locked} onClick={() => setSheet('chars')}>
              {uiText('Персонажи')}
              <StudioIcon kind="chev" />
            </button>
          ) : (
            roleTip(role) && <em>{uiText(roleTip(role))}</em>
          )}
        </div>
        <FadeRow className="ps-fr nosb" label={uiFormat('Кто играет: {0}', role.name)} watch={`${role.id}:${inRole.join()}:${performers.length}`}>
          {me && fitting(me) && (
            <span className="ps-fw" data-k="me">
              {face(me, 'me')}
              <button type="button" className="ps-fsw" aria-label={uiText('Каким вы будете')} disabled={locked} onClick={() => setSheet('self')}>
                <StudioIcon kind="swap" />
              </button>
            </span>
          )}
          {people.map(p => face(p, `p:${p.ref.slice('person:'.length)}`))}
          {tool('add', 'Добавить', 'Добавить близкого', () => {
            setEditing(null);
            setSheet('person');
          })}
          {role.allowChar && <i className="ps-fsep" data-k="sep" aria-hidden="true" />}
          {rowChars.map(p => face(p, `c:${p.ref.slice('character:'.length)}`))}
          {role.allowChar && chars.length > 0 && tool('all', 'Все', 'Все', () => setSheet('chars'))}
        </FadeRow>
      </div>
      {hint && (
        <p ref={hintRef} className="rl-tip" role="status">
          {uiText(hint)}
        </p>
      )}
      {sheet === 'chars' && (
        <StudioSheet title={uiFormat('Роль «{0}»', role.name)} onClose={() => setSheet(null)}>
          <h2>{uiFormat('Роль «{0}»', role.name)}</h2>
          <p className="studio-settings-sub">
            {uiText(role.max > 1 ? uiFormat('{0} из {1}', (cast[role.id] ?? []).length, role.max) : role.gender ? (role.gender === 'm' ? 'мужская роль' : 'женская роль') : '')}
          </p>
          <label className="search">
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={uiFormat('Искать среди {0} персонажей', chars.length)}
              aria-label={uiText('Поиск персонажа')}
            />
          </label>
          <div className="studio-native-person-grid">
            {sortedChars.map(p => {
              const reason = ineligible(role, p, charNames);
              const on = (cast[role.id] ?? []).includes(p.ref);
              return (
                <button
                  key={p.ref}
                  type="button"
                  className={`ps-fc${on ? ' on' : ''}${reason ? ' off' : ''}`}
                  aria-pressed={on}
                  aria-disabled={reason ? 'true' : undefined}
                  onClick={() => tap(p.ref, true)}
                >
                  <span className="ps-fo">
                    <span className="ps-fi" aria-hidden="true">
                      {artworkUrl(p.image) ? <img src={artworkUrl(p.image)!} alt="" /> : <span className="ini">{p.name.slice(0, 1)}</span>}
                    </span>
                  </span>
                  <b>{p.name}</b>
                  {playing(p) && <small>{uiFormat('Играет: {0}', playing(p)!)}</small>}
                </button>
              );
            })}
          </div>
          {!sortedChars.length && <p role="status">{uiText('Ничего не нашлось')}</p>}
          {hint && <p role="status">{uiText(hint)}</p>}
        </StudioSheet>
      )}
      {sheet === 'self' && (
        <SelfPhotosSheet
          self={self}
          onClose={() => setSheet(null)}
          onSaved={() => {
            setSheet(null);
            onReload?.();
          }}
        />
      )}
      {sheet === 'person' && (
        <PersonSheet
          person={editing}
          allowPets={role.kinds.includes('pet')}
          onClose={() => setSheet(null)}
          onSaved={saved => {
            setSheet(null);
            pending.current = { ref: `person:${saved.id}`, role: role.id };
            onReload?.();
          }}
        />
      )}
      {sheet === 'people' && (
        <StudioSheet
          title="Люди"
          onClose={() => setSheet(null)}
          footer={
            <button type="button" className="btn" onClick={() => setSheet(null)}>
              {uiText('Готово')}
            </button>
          }
        >
          <h2>{uiText('Люди')}</h2>
          <p className="studio-settings-sub">{uiText('Имя и фото · роли выбираются в ряду')}</p>
          <ul className="pp-ls">
            {performers
              .filter(p => p.type !== 'char')
              .map(p => {
                const played = roleOf(cast, p.ref);
                return (
                  <li key={p.ref} className={`pp-r${p.ready ? '' : ' np'}`}>
                    <span className="pp-rav" aria-hidden="true">
                      <Face who={p} />
                    </span>
                    <span className="pp-rtx">
                      <b>{p.name}</b>
                      <small>{uiText(p.ready ? (p.sub ?? '') : (p.notReady ?? 'нет фото'))}</small>
                    </span>
                    <em>{played ? uiText(roles.find(r => r.id === played)?.name ?? '') : ''}</em>
                  </li>
                );
              })}
          </ul>
          <p className="hint">{uiText('Фото и близких можно изменить в «Профиле» → «Моё».')}</p>
        </StudioSheet>
      )}
    </div>
  );
}
